import React from 'react';
import {
  HeaderActions,
  HeaderRow,
  HeaderSub,
  HeaderTitle,
} from './page-header.styled';
import { BackBar, BackButton } from './back-button';

/**
 * The shared screen header: title, optional subtitle, optional page actions,
 * and THE back button.
 *
 * The back control is <BackButton>, the product's single one — see its header
 * for why there is exactly one design and why it is always top left. This
 * component no longer draws its own: it used to own a `BackAction` pill, which
 * was one of the five treatments the unification pass collapsed.
 *
 * Back is an explicit destination (`backTo`, default /main-menu), never
 * navigate(-1) and never history sniffing. A concierge who opens the screen
 * from a deep link, a refresh, or a fresh tab has no in-app history entry to go
 * back to, so navigate(-1) would walk them out of the app entirely.
 *
 * WHERE IT SITS: its own <BackBar> row ABOVE the title, left aligned, so "top
 * left" is a property of this layout and not something each page has to
 * remember. It is deliberately NOT the first child of the title row: that row
 * is `space-between` with the heading anchored left, and squeezing back in
 * beside the heading would either shove the title rightwards or collapse into
 * the heading on a narrow screen. A separate row keeps both left-aligned and
 * keeps the wrap behaviour of <HeaderActions> untouched.
 *
 * <HeaderActions> now carries ONLY the page's own controls.
 */

export interface ScreenHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Where "back" goes. An explicit route, not a history offset. */
  backTo?: string;
  backLabel?: string;
  /** Page-specific controls, rendered on the right of the title row. */
  actions?: React.ReactNode;
}

export const ScreenHeader: React.FC<ScreenHeaderProps> = ({
  title,
  subtitle,
  backTo = '/main-menu',
  backLabel = 'Back to menu',
  actions,
}) => (
  <>
    <BackBar>
      <BackButton to={backTo} label={backLabel} />
    </BackBar>
    <HeaderRow>
      <div>
        <HeaderTitle>{title}</HeaderTitle>
        {subtitle ? <HeaderSub>{subtitle}</HeaderSub> : null}
      </div>
      {actions ? <HeaderActions>{actions}</HeaderActions> : null}
    </HeaderRow>
  </>
);
